/**
 * MeasureTool — two-click distance measurement with model snapping.
 *
 * Click two points (snapped to true vertices / midpoints / edges, same
 * candidates the move tool uses) → overlay line + markers + a floating
 * readout with the distance and ΔX/ΔY/ΔZ. A third click starts the next
 * measurement; Esc or re-clicking the toolbar button exits.
 */

import * as THREE from 'three/webgpu';

import { collectEdgeSnaps3D } from '@core/geometry/snap';
import type { Viewport } from '@render/viewport';
import { ensureNormals } from '@render/meshView';
import type { CadDocument } from '@core/document';

const SNAP_PX = 12;

type P3 = readonly [number, number, number];

const MARKER_MATERIAL = new THREE.MeshBasicMaterial({ color: 0xe8c66a, depthTest: false });
const LINE_MATERIAL = new THREE.LineBasicMaterial({
  color: 0xe8c66a,
  depthTest: false,
  transparent: true,
  opacity: 0.95,
});

export class MeasureTool {
  active = false;
  /** First picked point of the in-progress measurement. */
  private a: P3 | null = null;
  private candidates: { p: P3; kind: string }[] = [];
  private readonly group = new THREE.Group();
  private readonly readout: HTMLDivElement;
  private readonly hoverBadge: HTMLDivElement;
  onStateChange: (() => void) | null = null;

  constructor(
    private readonly viewport: Viewport,
    private readonly host: { readonly doc: CadDocument },
  ) {
    this.group.renderOrder = 1200;
    this.readout = document.createElement('div');
    this.readout.className = 'nv-measure';
    this.readout.style.display = 'none';
    viewport.element.appendChild(this.readout);
    this.hoverBadge = document.createElement('div');
    this.hoverBadge.className = 'nv-snap-badge';
    this.hoverBadge.style.display = 'none';
    viewport.element.appendChild(this.hoverBadge);
    viewport.element.addEventListener('pointermove', this.onHover);
  }

  toggle(): void {
    if (this.active) this.stop();
    else this.start();
  }

  start(): void {
    this.active = true;
    this.a = null;
    this.refreshCandidates();
    this.viewport.overlay.add(this.group);
    this.onStateChange?.();
  }

  stop(): void {
    this.active = false;
    this.a = null;
    this.group.clear();
    this.group.removeFromParent();
    this.readout.style.display = 'none';
    this.hoverBadge.style.display = 'none';
    this.viewport.requestRender();
    this.onStateChange?.();
  }

  get hint(): string {
    if (!this.active) return '';
    return this.a
      ? 'Measure: click the second point (snaps to vertices, midpoints, edges) · Esc exits'
      : 'Measure: click the first point (snaps to vertices, midpoints, edges) · Esc exits';
  }

  /** Rebuild snap candidates from the last evaluation (call on doc change). */
  refreshCandidates(): void {
    this.candidates = [];
    const report = this.host.doc.lastReport;
    if (!report) return;
    for (const [id, result] of report.results) {
      if (result.kind !== 'body' || report.consumed.has(id)) continue;
      if (result.mesh.edgePositions.length === 0) continue;
      const { points } = collectEdgeSnaps3D(
        result.mesh.edgePositions,
        result.mesh.vertexPositions,
      );
      for (const pt of points) this.candidates.push({ p: pt.p as P3, kind: pt.kind });
    }
    this.candidates.push({ p: [0, 0, 0], kind: 'origin' });
  }

  /** Snap a client-space point: nearest candidate within SNAP_PX, else the
   * raycast body point under the cursor, else null. */
  private pick(clientX: number, clientY: number): { p: P3; kind: string } | null {
    const rect = this.viewport.element.getBoundingClientRect();
    const cam = this.viewport.camera;
    let best: { p: P3; kind: string } | null = null;
    let bestD = SNAP_PX;
    const v = new THREE.Vector3();
    for (const c of this.candidates) {
      v.set(c.p[0], c.p[1], c.p[2]).project(cam);
      if (v.z > 1) continue; // behind the camera
      const sx = ((v.x + 1) / 2) * rect.width + rect.left;
      const sy = ((1 - v.y) / 2) * rect.height + rect.top;
      const d = Math.hypot(sx - clientX, sy - clientY);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    if (best) return best;
    const hit = this.viewport.raycastBodies(clientX, clientY);
    if (hit) return { p: [hit.point.x, hit.point.y, hit.point.z], kind: 'on-face' };
    return null;
  }

  /** Snap marker + label while hovering. */
  private onHover = (e: PointerEvent): void => {
    if (!this.active) return;
    if (
      e.target instanceof Element &&
      e.target.closest('.nv-side, .nv-props, .nv-toolbar, .nv-hint, .nv-dim, .nv-menu, .nv-section')
    ) {
      this.hoverBadge.style.display = 'none';
      return;
    }
    const picked = this.pick(e.clientX, e.clientY);
    // Re-draw persistent markers plus the hover cue.
    this.draw(picked?.p ?? null);
    if (picked && picked.kind !== 'on-face') {
      const rect = this.viewport.element.getBoundingClientRect();
      this.hoverBadge.textContent = picked.kind;
      this.hoverBadge.style.display = 'block';
      this.hoverBadge.style.left = `${e.clientX - rect.left + 14}px`;
      this.hoverBadge.style.top = `${e.clientY - rect.top - 26}px`;
    } else {
      this.hoverBadge.style.display = 'none';
    }
  };

  /** A viewport tap while active. Returns true (always consumes the tap). */
  handleTap(clientX: number, clientY: number): boolean {
    const picked = this.pick(clientX, clientY);
    if (!picked) return true;
    if (!this.a) {
      this.a = picked.p;
      this.readout.style.display = 'none';
    } else {
      const a = this.a;
      const b = picked.p;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const dz = b[2] - a[2];
      const d = Math.hypot(dx, dy, dz);
      const f = (v: number): string => (Math.round(v * 100) / 100).toString();
      this.readout.innerHTML =
        `<b>${f(d)} mm</b>` +
        `<span>ΔX ${f(dx)} · ΔY ${f(dy)} · ΔZ ${f(dz)}</span>`;
      const rect = this.viewport.element.getBoundingClientRect();
      this.readout.style.display = 'block';
      this.readout.style.left = `${clientX - rect.left + 14}px`;
      this.readout.style.top = `${clientY - rect.top + 10}px`;
      this.drawMeasurement(a, b);
      this.a = null; // next click starts a new measurement
      this.onStateChange?.();
      return true;
    }
    this.draw(null);
    this.onStateChange?.();
    return true;
  }

  private marker(p: P3, r = 0.9): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 10), MARKER_MATERIAL);
    m.position.set(p[0], p[1], p[2]);
    m.renderOrder = 1200;
    m.raycast = () => undefined;
    return m;
  }

  /** Redraw transient state: first point marker + optional hover marker. */
  private draw(hover: P3 | null): void {
    // Keep a completed measurement on screen (line group named 'done').
    const done = this.group.children.filter((c) => c.name === 'done');
    this.group.clear();
    for (const c of done) this.group.add(c);
    if (this.a) this.group.add(this.marker(this.a));
    if (hover) this.group.add(this.marker(hover, 0.6));
    this.viewport.requestRender();
  }

  private drawMeasurement(a: P3, b: P3): void {
    this.group.clear();
    const g = new THREE.Group();
    g.name = 'done';
    g.add(this.marker(a), this.marker(b));
    const geo = ensureNormals(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(...a),
        new THREE.Vector3(...b),
      ]),
    );
    const line = new THREE.Line(geo, LINE_MATERIAL);
    line.renderOrder = 1200;
    line.raycast = () => undefined;
    g.add(line);
    this.group.add(g);
    this.viewport.requestRender();
  }
}
